import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { InventoryItem } from '../types';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

const InventoryItems: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    unit: '',
    description: ''
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const data = await apiGet<{ items?: InventoryItem[] }>('/inventory-items');
      
      // Для каждого ТМЦ получаем актуальный остаток
      const itemsWithBalances = await Promise.all(
        (data.items || []).map(async (item: InventoryItem) => {
          try {
            const balanceData = await apiGet<{ balance?: number }>(`/inventory-items/${item.id}/balance?mode=live`);
            return {
              ...item,
              balanceQuantity: balanceData.balance || 0
            };
          } catch (err) {
            console.error(`Ошибка получения остатка для ${item.name}:`, err);
            return item;
          }
        })
      );
      
      setItems(itemsWithBalances);
    } catch (err) {
      setError('Ошибка загрузки ТМЦ');
      console.error('Ошибка:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await apiPut(`/inventory-items/${editingItem.id}`, formData);
      } else {
        await apiPost('/inventory-items', formData);
      }
      
      await fetchItems();
      setShowForm(false);
      setEditingItem(null);
      resetForm();
    } catch (err: any) {
      setError('Ошибка сохранения ТМЦ');
      console.error('Ошибка:', err);
    }
  };

  const handleEdit = (item: InventoryItem) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      category: item.category,
      unit: item.unit,
      description: item.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить ТМЦ?')) return;
    
    try {
      await apiDelete(`/inventory-items/${id}`);
      await fetchItems();
    } catch (err: any) {
      // Есть связанные документы — предлагаем деактивировать вместо удаления
      if (err.message?.includes('связанные документы') || err.message?.includes('400')) {
        const proceedDeactivate = confirm('Нельзя удалить ТМЦ: есть связанные документы. Деактивировать элемент?');
        if (proceedDeactivate) {
          try {
            await apiPut(`/inventory-items/${id}`, { isActive: false });
            await fetchItems();
          } catch (deactivateErr: any) {
            setError('Не удалось деактивировать ТМЦ');
            console.error('Ошибка:', deactivateErr);
          }
        }
        return;
      }
      setError('Ошибка удаления ТМЦ');
      console.error('Ошибка:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      unit: '',
      description: ''
    });
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'мячи': return '🎾';
      case 'вода': return '💧';
      case 'кубки': return '🏆';
      case 'трофеи': return '🏆';
      case 'оборудование': return '⚙️';
      case 'инвентарь': return '🎯';
      default: return '📦';
    }
  };

  if (loading) return <div className="flex justify-center items-center h-64">Загрузка...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">📦 Товарно-материальные ценности</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/inventory-return-settings')}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
          >
            ⚙️ Настройки возврата
          </button>
          <button
            onClick={() => {
              resetForm();
              setEditingItem(null);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            ➕ Добавить ТМЦ
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((item) => (
          <div key={item.id} className="bg-white shadow rounded-lg p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{getCategoryIcon(item.category)}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                  <p className="text-sm text-gray-500">{item.category}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(item)}
                  className="text-blue-600 hover:text-blue-900 text-sm"
                >
                  ✏️
                </button>
                {/* Показываем кнопку удаления только если нет документов */}
                {(!item.documentsCount || 
                  (item.documentsCount.purchaseDocuments === 0 && 
                   item.documentsCount.consumptionDocuments === 0 && 
                   item.documentsCount.movements === 0)) && (
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="text-red-600 hover:text-red-900 text-sm"
                  >
                    🗑️
                  </button>
                )}
              </div>
            </div>
            
              <div className="mt-4 space-y-2">
              {item.description && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Описание:</span> {item.description}
                </div>
              )}
              
              {typeof item.balanceQuantity === 'number' && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Остаток:</span>
                  <span className="font-medium">{item.balanceQuantity} {item.unit}</span>
                </div>
              )}

              {item.documentsCount && (
                <div className="grid grid-cols-3 gap-2 text-xs mt-2">
                  <div className="bg-gray-50 border border-gray-200 rounded px-2 py-1 text-center">
                    <div className="text-gray-500">Закупок</div>
                    <div className="font-semibold">{item.documentsCount.purchaseDocuments}</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded px-2 py-1 text-center">
                    <div className="text-gray-500">Расходов</div>
                    <div className="font-semibold">{item.documentsCount.consumptionDocuments}</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded px-2 py-1 text-center">
                    <div className="text-gray-500">Движений</div>
                    <div className="font-semibold">{item.documentsCount.movements}</div>
                  </div>
                </div>
              )}

              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Статус:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  item.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {item.isActive ? 'Активен' : 'Неактивен'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Нет ТМЦ</h3>
          <p className="text-gray-500">Добавьте товарно-материальные ценности для начала работы</p>
        </div>
      )}

      {/* Модальное окно формы */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingItem ? 'Редактировать ТМЦ' : 'Добавить ТМЦ'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Категория *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Выберите категорию</option>
                  <option value="Мячи">🎾 Мячи</option>
                  <option value="Вода">💧 Вода</option>
                  <option value="Кубки">🏆 Кубки</option>
                  <option value="Трофеи">🏆 Трофеи</option>
                  <option value="Оборудование">⚙️ Оборудование</option>
                  <option value="Инвентарь">🎯 Инвентарь</option>
                  <option value="Другое">📦 Другое</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Единица измерения *
                </label>
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Выберите единицу</option>
                  <option value="шт">шт (штуки)</option>
                  <option value="кг">кг (килограммы)</option>
                  <option value="л">л (литры)</option>
                  <option value="м">м (метры)</option>
                  <option value="м²">м² (квадратные метры)</option>
                  <option value="м³">м³ (кубические метры)</option>
                  <option value="упак">упак (упаковки)</option>
                  <option value="компл">компл (комплекты)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingItem(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingItem ? 'Обновить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryItems;
